const buildClasses = (
  baseClass: string,
  elementClass: string | undefined,
  modifierClasses: any[],
): string => {
  const prefix = `${baseClass}${elementClass ?? ""}`;
  return (
    prefix +
    " " +
    modifierClasses
      .map((i) => {
        if (typeof i === "string") {
          if (!i) return null;
          if (i.length > 0 && i[0] === "!") return i.substring(1);
          return prefix + i;
        }
        if (typeof i === "object")
          return Object.keys(i)
            .map((modifier) => {
              const condition = i[modifier];
              if (!condition) return null;
              if (modifier.length > 0 && modifier[0] === "!")
                return modifier.substring(1);
              return prefix + modifier;
            })
            .filter((className) => className !== null)
            .join(" ");
        return null;
      })
      .filter((className) => className !== null)
      .join(" ")
  );
};

/**
 * Creates a function that combines BEM (Block, Element, Modifier) with the functionality found in the classnames library.
 * This makes BEM more concise and clean.
 *
 * The base class will be a prefix for all the other classes passed in. Example:
 * const [expanded, setExpanded] = useState('false');
 * const bem = createBem('component-base-class');
 * |return (
 * |<div className={bem('')}>
 * | <div> className={bem('__expander', '--theme-primary', {'--expanded': expanded})}>
 * |     This text will display when expanded
 * |  </div>
 * |</div>);
 *
 * The outer div will get the base class (component-base-class). The inner div will get
 * 'component-base-class__expander component-base-class__expander--theme-primary' in any
 * case, and 'component-base-class__expander--expanded' if the 'expanded' variable is true
 *
 * If you need a class outside of BEM, you can prefix it with !. bem("", "!epi-editContainer")
 * will return the class epi-editContainer without a prefix
 *
 * @param baseClass The baseClass parameter will prefix all classes passed in
 * @returns A function that takes an element class and modifiers as strings or objects that
 * will resolve to classnames
 */
const createBem =
  (
    baseClass: string,
  ): ((
    elementClass: string | undefined,
    ...modifierClasses: any[]
  ) => string) =>
  (elementClass: string | undefined, ...modifierClasses: []): string =>
    buildClasses(baseClass, elementClass, modifierClasses);

/**
 * Like createBem, but wraps the CSS classes in an object like this:
 * {className: "block__element block__element--modifier"}
 * This can be used with the spread operator: <div {...bem("__element", {'--modifier': modifierActive})} />
 * !!!IMPORTANT!!! The className from the spread operator can easily be overridden or override other
 * className attributes. This will completely override the existing class: <div className="existing-class" {...bem("__element")} />
 * @param baseClass The baseClass parameter will prefix all the classes passed in
 * @returns A function that takes an element class and modifiers as strings or objects that
 * will resolve to classnames as a string wrapped in an object like this:
 * { className : 'block__element block__element--modifier'}
 */
export const createSpreadableBem =
  (
    baseClass: string,
  ): ((
    elementClass: string | undefined,
    ...modifierClasses: any[]
  ) => { className: string }) =>
  (
    elementClass: string | undefined,
    ...modifierClasses: []
  ): { className: string } => ({
    className: buildClasses(baseClass, elementClass, modifierClasses),
  });

export default createBem;
